/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

/**
 * The interface for the props for the account statement request modal.
 */
interface IAccountStatementRequestModalProps {
    email: string;
    isModalOpen: boolean;
    fromDateString: string;
    toDateString: string;
    canSubmitRequest: boolean;
    modalHeaderLabel: string;
    sendToEmailLabel: string;
    fromDateLabel: string;
    toDateLabel: string;
    submitRequestLabel: string;
    cancelLabel: string;
    className: string;
    selectedUser: string;
    selectedUserLabel: string;
    toggle(): void;
    onChangeFromDate(newFromDate: string): void;
    onChangeToDate(newToDate: string): void;
    onSubmitRequest(): void;
}

export const RequestStatementModal: React.FC<IAccountStatementRequestModalProps> = ({ className, email, isModalOpen, toggle, fromDateString, toDateString, onChangeFromDate, onChangeToDate, canSubmitRequest,
    modalHeaderLabel, sendToEmailLabel, fromDateLabel, toDateLabel, submitRequestLabel, cancelLabel, onSubmitRequest, selectedUser, selectedUserLabel }) => {

    const onChangeFromDateEvent = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        onChangeFromDate(value);
    }, [onChangeFromDate]);

    const onChangeToDateEvent = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        onChangeToDate(value);
    }, [onChangeToDate]);

    /**
     * If the onBlur is called on the fromDate and the date has been changed, reset focus on it.
     * @param event - The event that is triggering onBlur.
     */
    const onBlurFromDate = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (ObjectExtensions.isFunction(event.target.focus) && event.target.value !== fromDateString) {
            event.target.focus();
        }
    }, [fromDateString]);

    /**
     * If the onBlur is called on the toDate and the date has been changed, reset focus on it.
     * @param event - The event that is triggering onBlur.
     */
    const onBlurToDate = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (ObjectExtensions.isFunction(event.target.focus) && event.target.value !== toDateString) {
            event.target.focus();
        }
    }, [toDateString]);

    return (
        <Modal
            className='msc-modal-input-required'
            isOpen={isModalOpen}
            horizontalPosition='center'
            verticalPosition='center'
            autoFocus
            fade={false}
            toggle={toggle}
        >
            <ModalHeader toggle={toggle}>
                {modalHeaderLabel}
            </ModalHeader>
            <ModalBody>
                <label htmlFor='sendToEmail'>
                    {sendToEmailLabel}
                </label>
                <br />
                <div id='sendToEmail' className={`${className}__sendToEmail`}>
                    {email}
                </div>
                {
                    selectedUser && (
                        <div>
                            {selectedUserLabel}
                            <span className={`${className}__selectedUser`}>
                                {selectedUser}
                            </span>
                        </div>
                    )
                }
                <div>
                    <div className={`${className}__requestStatement__fromDate`}>
                        <label htmlFor='fromDateSelector'>
                            {fromDateLabel}
                        </label>
                        <input
                            id='fromDateSelector' type='date' value={fromDateString}
                            onBlur={onBlurFromDate}
                            onChange={onChangeFromDateEvent} max={toDateString} className={`${className}__dateSelector form-control`} />
                    </div>
                    <div className={`${className}__requestStatement__toDate`}>
                        <label htmlFor='toDateSelector'>
                            {toDateLabel}
                        </label>
                        <input
                            id='toDateSelector' type='date' value={toDateString}
                            onBlur={onBlurToDate}
                            onChange={onChangeToDateEvent} min={fromDateString} className={`${className}__dateSelector form-control`} />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button
                    className={`${className}__action__submitRequest`}
                    onClick={onSubmitRequest}
                    disabled={!canSubmitRequest}
                    aria-disabled={!canSubmitRequest}
                >
                    {submitRequestLabel}
                </button>
                {' '}
                <button className={`${className}__action__cancelRequest`} onClick={toggle}>
                    {cancelLabel}
                </button>
            </ModalFooter>
        </Modal>
    );
};
